import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useRef, useState } from 'react'
import {
  PlanBotTaskStatus,
  getCarrierTasks,
  type CarrierTaskSortField,
  type SortOrder,
  type TaskProgress,
  type TaskResponseDto,
} from '../api/fetch-carrier-api'

export type { TaskResponseDto }

export const DEFAULT_PAGE_SIZE = 20

const POLL_INTERVAL_MS = 30000

// Generous cap on how many carriers can be fetching at once (well under the
// API's max limit of 100). Must be high enough to cover every currently
// running task — if it's too low, the same "only the first N show live
// progress" bug this hook exists to fix comes right back.
const MAX_CONCURRENT_RUNNING_TASKS = 20

const DEFAULT_SORT: CarrierTaskSortField = 'createdAt'
const DEFAULT_ORDER: SortOrder = 'DESC'

const ACTIVE_STATUSES = new Set<PlanBotTaskStatus>([
  PlanBotTaskStatus.QUEUED,
  PlanBotTaskStatus.IN_PROGRESS,
])

function hasActiveRows(rows: TaskResponseDto[]): boolean {
  return rows.some((t) => ACTIVE_STATUSES.has(t.status))
}

export interface CachedTaskProgress {
  taskId: string
  progress: TaskProgress | null
}

/**
 * Merges live progress into every active row, not just one. `runningTasks`
 * can contain several entries when multiple carriers are fetching at the
 * same time — each is matched to its own row by id so every visible progress
 * bar moves, instead of only the row that happens to be the first result.
 *
 * @internal exported for unit tests
 */
export function mergeTaskRowsWithRunningProgress(
  baseRows: TaskResponseDto[],
  runningTasks: TaskResponseDto[],
  cachedProgress: CachedTaskProgress[]
): TaskResponseDto[] {
  const runningById = new Map(runningTasks.map((task) => [task.id, task]))
  const cachedById = new Map(
    cachedProgress.map((cached) => [cached.taskId, cached])
  )
  return baseRows.map((row) => {
    const runningTask = runningById.get(row.id)
    if (runningTask) {
      return { ...row, progress: runningTask.progress }
    }
    const cached = cachedById.get(row.id)
    if (cached && ACTIVE_STATUSES.has(row.status) && cached.progress) {
      return { ...row, progress: cached.progress }
    }
    return row
  })
}

export function useCarrierTasks() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sort, setSort] = useState<CarrierTaskSortField>(DEFAULT_SORT)
  const [order, setOrder] = useState<SortOrder>(DEFAULT_ORDER)

  const {
    data,
    isLoading: isInitialLoading,
    isPlaceholderData,
    error,
  } = useQuery({
    queryKey: ['carrierTasks', page, pageSize, sort, order],
    queryFn: () =>
      getCarrierTasks({ page, limit: pageSize, sortBy: sort, order }),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const rows = query.state.data?.data ?? []
      if (hasActiveRows(rows)) return POLL_INTERVAL_MS
      return false
    },
  })

  const { data: runningCheck } = useQuery({
    queryKey: ['carrierTasks', 'runningCheck'],
    queryFn: () =>
      getCarrierTasks({
        isRunning: true,
        limit: MAX_CONCURRENT_RUNNING_TASKS,
      }),
    refetchInterval: (query) => {
      const total = query.state.data?.meta?.total ?? 0
      return total > 0 ? POLL_INTERVAL_MS : false
    },
  })

  // The paginated list endpoint doesn't compute per-pass `progress` for
  // every row (likely a perf trade-off), but the `isRunning=true` lookup
  // does return it for every currently running task. Merge each one into
  // its matching row so every active progress bar reflects real data
  // instead of only whichever task happened to be first in the response.
  // Cache the last-seen progress per task so a bar doesn't snap back to 0%
  // in the gap between that task finishing and the paginated list refetch.
  const lastRunningProgressRef = useRef<Map<string, TaskProgress | null>>(
    new Map()
  )
  const runningTasks = useMemo(() => runningCheck?.data ?? [], [runningCheck])

  for (const task of runningTasks) {
    lastRunningProgressRef.current.set(task.id, task.progress)
  }

  const rows = useMemo(() => {
    const baseRows = data?.data ?? []
    const cache = lastRunningProgressRef.current
    const merged = mergeTaskRowsWithRunningProgress(
      baseRows,
      runningTasks,
      Array.from(cache, ([taskId, progress]) => ({ taskId, progress }))
    )

    for (const taskId of Array.from(cache.keys())) {
      const cachedRow = baseRows.find((row) => row.id === taskId)
      if (cachedRow && !ACTIVE_STATUSES.has(cachedRow.status)) {
        cache.delete(taskId)
      }
    }

    return merged
  }, [data, runningTasks])

  const handlePageChange = useCallback(
    (newPage: number, newPageSize?: number) => {
      setPage(newPage)
      if (newPageSize !== undefined && newPageSize !== pageSize) {
        setPageSize(newPageSize)
        setPage(1)
      }
    },
    [pageSize]
  )

  const handleSortChange = useCallback(
    (field: CarrierTaskSortField, dir: SortOrder) => {
      setSort(field)
      setOrder(dir)
      setPage(1)
    },
    []
  )

  return {
    data: rows,
    total: data?.meta?.total ?? 0,
    isLoading: isInitialLoading || isPlaceholderData,
    error,
    page,
    pageSize,
    sort,
    order,
    handlePageChange,
    handleSortChange,
  }
}

export default useCarrierTasks
